import React, { useEffect, useState } from 'react';
import { BracketSeedLogo } from '../common/BracketSeedLogo';

type LineType = 'cmd' | 'brand' | 'dim' | 'ok' | 'git' | 'kv' | 'quote' | 'blank';
interface TermLine { type: LineType; text?: string; key?: string; value?: string; }

const LINES: TermLine[] = [
  { type: 'cmd',   text: 'PS C:\\code-garden\\admin> .\\Start-Portal.ps1' },
  { type: 'blank' },
  { type: 'brand', text: '◆  CODE GARDEN  v1.0.1' },
  { type: 'dim',   text: 'Custom Software Studio · Cape Town, ZA' },
  { type: 'blank' },
  { type: 'cmd',   text: 'PS C:\\code-garden\\admin> Invoke-SystemCheck -All' },
  { type: 'blank' },
  { type: 'ok',    text: '[✓] API Gateway .................. online' },
  { type: 'ok',    text: '[✓] Invoice Engine ............... ready' },
  { type: 'ok',    text: '[✓] Client Registry .............. 3 active' },
  { type: 'ok',    text: '[✓] Auth Service ................. secure' },
  { type: 'ok',    text: '[✓] Audit Logger ................. logging' },
  { type: 'blank' },
  { type: 'cmd',   text: 'PS C:\\code-garden\\admin> git log --oneline -4' },
  { type: 'blank' },
  { type: 'git',   text: 'a4f2b1c  feat: automated invoice generation' },
  { type: 'git',   text: '3d891ef  fix: VAT calculation precision' },
  { type: 'git',   text: 'b72c4a1  feat: accounts pack consolidation' },
  { type: 'git',   text: '2c8d5f9  chore: production deploy v1.0.1' },
  { type: 'blank' },
  { type: 'cmd',   text: 'PS C:\\code-garden\\admin> Get-SystemStats' },
  { type: 'blank' },
  { type: 'kv',    key: 'Invoices generated', value: '126' },
  { type: 'kv',    key: 'Total invoiced',     value: 'R 1,872,950' },
  { type: 'kv',    key: 'Active clients',     value: '3' },
  { type: 'kv',    key: 'System uptime',      value: '847 days' },
  { type: 'blank' },
  { type: 'quote', text: '"Software built to run your business."' },
  { type: 'blank' },
];

const LINE_DELAY: Record<LineType, number> = {
  cmd: 400, blank: 70, brand: 55, dim: 55, ok: 55, git: 55, kv: 55, quote: 55,
};

function TerminalLine({ line }: { line: TermLine }) {
  const base = "font-['Space_Mono'] text-[12.5px] leading-[1.7]";
  if (line.type === 'blank')  return <div className={`${base} min-h-[1.4em]`} />;
  if (line.type === 'cmd')    return <div className={`${base} text-accent font-bold`}>{line.text}</div>;
  if (line.type === 'brand')  return <div className={`${base} text-accent font-bold`}>{line.text}</div>;
  if (line.type === 'dim')    return <div className={`${base} text-muted2`}>{line.text}</div>;
  if (line.type === 'ok')     return <div className={`${base} text-accent`}>{line.text}</div>;
  if (line.type === 'git')    return <div className={`${base} text-muted2`}>{line.text}</div>;
  if (line.type === 'quote')  return <div className={`${base} text-muted italic`}>{line.text}</div>;
  if (line.type === 'kv')     return (
    <div className={`${base} flex`}>
      <span className="text-muted2 min-w-[210px]">{line.key}</span>
      <span className="text-accent font-bold">→  {line.value}</span>
    </div>
  );
  return null;
}

function TerminalPanel() {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    if (visible >= LINES.length) return;
    const t = setTimeout(() => setVisible(v => v + 1), LINE_DELAY[LINES[visible].type]);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <div
      className="hidden lg:flex flex-col relative flex-shrink-0 border-r border-line overflow-hidden"
      style={{
        width: '58%',
        backgroundImage: 'radial-gradient(circle, rgba(146,222,113,.17) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        backgroundColor: '#070A09',
      }}
    >
      {/* Glow */}
      <div className="absolute top-0 left-0 w-[480px] h-[480px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(146,222,113,.07), transparent 65%)' }} />

      {/* Top meta bar */}
      <div className="absolute top-8 left-8 right-8 flex justify-between z-10">
        <span className="cg-label">Code-Garden / Admin Portal</span>
        <span className="cg-label">Cape Town, ZA ✳ Remote-first</span>
      </div>

      {/* Terminal window */}
      <div className="relative z-10 m-auto w-full max-w-[580px] px-10">
        {/* Chrome bar */}
        <div className="flex items-center justify-between h-9 bg-[#1a1a1a]">
          <div className="flex h-full">
            <div className="flex items-center gap-1.5 px-4 bg-[#060908] border-t-2 border-t-accent border-r border-r-[rgba(146,222,113,0.08)]">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <rect x="1" y="3" width="10" height="7" stroke="#92de71" strokeWidth="1"/>
                <path d="M4 3V2h4v1" stroke="#92de71" strokeWidth="1"/>
                <path d="M3 6l2 2-2 2" stroke="#92de71" strokeWidth="1" strokeLinecap="round"/>
              </svg>
              <span className="font-['Space_Mono'] text-[10px] text-[#c8c8c8]">PowerShell</span>
              <span className="font-['Space_Mono'] text-[10px] text-muted2 ml-1">✕</span>
            </div>
          </div>
          <div className="flex">
            {['—','□','✕'].map((c, i) => (
              <div key={i} className={`w-11 h-9 flex items-center justify-center font-['Space_Mono'] text-[13px] ${i === 2 ? 'text-[#c8c8c8]' : 'text-[#666]'}`}>{c}</div>
            ))}
          </div>
        </div>
        {/* Terminal body */}
        <div className="bg-[#060908] border border-[rgba(146,222,113,0.07)] border-t-0 p-5 min-h-[340px]">
          {LINES.slice(0, visible).map((line, i) => <TerminalLine key={i} line={line} />)}
          {visible < LINES.length && <span className="terminal-cursor">█</span>}
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-8 left-8 z-10">
        <span className="cg-label">© {new Date().getFullYear()} Code Garden · All rights reserved</span>
      </div>
    </div>
  );
}

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => (
  <div className="flex h-screen bg-bg overflow-hidden">
    <TerminalPanel />
    <div className="flex-1 bg-panel flex items-center justify-center overflow-auto">
      <div className="relative w-full max-w-[360px] px-6 py-10">
        {/* Crosshair corners */}
        {[{ top: -14, left: -14 }, { top: -14, right: -14 }, { bottom: -14, left: -14 }, { bottom: -14, right: -14 }].map((pos, i) => (
          <div key={i} style={{ position: 'absolute', width: 9, height: 9, ...pos, zIndex: 2, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, width: 1, height: '100%', background: '#92de71', transform: 'translateX(-0.5px)' }} />
            <div style={{ position: 'absolute', top: '50%', left: 0, height: 1, width: '100%', background: '#92de71', transform: 'translateY(-0.5px)' }} />
          </div>
        ))}
        <div className="flex justify-center mb-7">
          <BracketSeedLogo variant="dark" size={56} />
        </div>
        {children}
      </div>
    </div>
  </div>
);

export default AuthLayout;
